import { ApiError } from './api'
import { beginPageLoad } from './pageLoading'
import { useCallback, useEffect, useRef, useState } from 'react'
interface Result<T> { data: T | null; error: string | null; errorStatus: number | null; loading: boolean; reload: () => void }
/** Keep stale data on refresh, but never carry a previous resource into a new route. */
export function useApi<T>(fetcher: () => Promise<T>, deps: unknown[] = [], pollMs?: number): Result<T> {
  const key = JSON.stringify(deps)
  const [state, setState] = useState({ key, data: null as T | null, error: null as string | null, errorStatus: null as number | null, loading: true })
  const [tick, setTick] = useState(0)
  const settledKey = useRef<string | null>(null)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  useEffect(() => {
    let cancelled = false
    let pending = false
    const finishLoading = settledKey.current === key ? () => {} : beginPageLoad()
    const run = async () => {
      if (pending) return
      pending = true
      try {
        const data = await fetcherRef.current()
        if (!cancelled) setState({ key, data, error: null, errorStatus: null, loading: false })
      } catch (err) {
        if (!cancelled) setState(previous => ({ key, data: previous.key === key ? previous.data : null,
          error: err instanceof Error ? err.message : String(err), errorStatus: err instanceof ApiError ? err.status : null, loading: false }))
      } finally {
        pending = false
        if (!cancelled) settledKey.current = key
        finishLoading()
      }
    }
    void run()
    const onOnline = () => { void run() }
    window.addEventListener('online', onOnline)
    const timer = pollMs ? setInterval(run, pollMs) : undefined
    return () => { window.removeEventListener('online', onOnline); cancelled = true; finishLoading(); if (timer) clearInterval(timer) }
  }, [key, tick, pollMs])
  const reload = useCallback(() => setTick(value => value + 1), [])
  return { ...(state.key === key ? state : { data: null, error: null, errorStatus: null, loading: true }), reload }
}
